import { Camera, Pen, Trash2, Upload } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar } from '@/components/ui/catalyst/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  useDeleteUserAvatarMutation,
  useDeleteUserSignatureMutation,
  useUploadUserAvatarMutation,
  useUploadUserSignatureMutation,
} from '@/services/api/userService';
import { selectUser } from '@/state/selectors/root-selectors';

export const ProfileSettingsPage: React.FC = () => {
  const user = useSelector(selectUser);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);

  const [uploadAvatar, { isLoading: isUploadingAvatar }] =
    useUploadUserAvatarMutation();
  const [deleteAvatar, { isLoading: isDeletingAvatar }] =
    useDeleteUserAvatarMutation();
  const [uploadSignature, { isLoading: isUploadingSignature }] =
    useUploadUserSignatureMutation();
  const [deleteSignature, { isLoading: isDeletingSignature }] =
    useDeleteUserSignatureMutation();

  const handleUploadAvatar = async (file: File) => {
    if (!user?.id) return;
    try {
      await uploadAvatar({ userId: user.id, file }).unwrap();
      toast.success('Avatar uploaded successfully');
      setAvatarModalOpen(false);
      window.location.reload(); // Reload to update user state
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error('Failed to upload avatar');
    }
  };

  const handleDeleteAvatar = async () => {
    if (!user?.id) return;
    try {
      await deleteAvatar(user.id).unwrap();
      toast.success('Avatar removed');
      window.location.reload();
    } catch (error) {
      console.error('Error deleting avatar:', error);
      toast.error('Failed to delete avatar');
    }
  };

  const handleUploadSignature = async (file: File) => {
    if (!user?.id) return;
    try {
      await uploadSignature({ userId: user.id, file }).unwrap();
      toast.success('Signature uploaded successfully');
      setSignatureModalOpen(false);
      window.location.reload();
    } catch (error) {
      console.error('Error uploading signature:', error);
      toast.error('Failed to upload signature');
    }
  };

  const handleDeleteSignature = async () => {
    if (!user?.id) return;
    try {
      await deleteSignature(user.id).unwrap();
      toast.success('Signature removed');
      window.location.reload();
    } catch (error) {
      console.error('Error deleting signature:', error);
      toast.error('Failed to delete signature');
    }
  };

  const displayName =
    user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`
      : user?.email || 'Account';

  const initials = user?.first_name
    ? `${user.first_name[0]}${user.last_name ? user.last_name[0] : ''}`
    : user?.email?.slice(0, 1);

  return (
    <div className="container max-w-4xl mx-auto py-8 px-4">
      <h1 className="text-2xl font-bold mb-6">Profile Settings</h1>

      <div className="space-y-6">
        {/* Avatar Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Profile Avatar
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-6">
              <Avatar
                className="size-24"
                src={user?.avatar || undefined}
                initials={initials}
              />
              <div className="flex-1">
                <h3 className="font-medium text-lg mb-1">{displayName}</h3>
                <p className="text-sm text-gray-500 mb-3">{user?.email}</p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => setAvatarModalOpen(true)}
                    className="gap-1"
                  >
                    <Upload className="h-3 w-3" />
                    {user?.avatar ? 'Change' : 'Upload'} Avatar
                  </Button>
                  {user?.avatar && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleDeleteAvatar}
                      disabled={isDeletingAvatar}
                      className="gap-1 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-3 w-3" />
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Signature Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Pen className="h-5 w-5" />
              Signature
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {user?.signature ? (
              <div className="space-y-3">
                <div className="border rounded-lg p-4 bg-gray-50">
                  <img
                    src={user.signature}
                    alt="Your signature"
                    className="max-w-xs max-h-24 object-contain"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setSignatureModalOpen(true)}
                    className="gap-1"
                  >
                    <Upload className="h-3 w-3" />
                    Update Signature
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDeleteSignature}
                    disabled={isDeletingSignature}
                    className="gap-1 text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-3 w-3" />
                    Remove
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-500 mb-3">
                  No signature uploaded yet
                </p>
                <Button
                  size="sm"
                  onClick={() => setSignatureModalOpen(true)}
                  className="gap-1"
                >
                  <Upload className="h-3 w-3" />
                  Upload Signature
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Avatar Upload Modal */}
      <ImageUploadModal
        isOpen={avatarModalOpen}
        onClose={() => setAvatarModalOpen(false)}
        onUpload={handleUploadAvatar}
        isUploading={isUploadingAvatar}
        title="Upload Profile Avatar"
        description="Choose a profile picture to display"
      />

      {/* Signature Upload Modal */}
      <ImageUploadModal
        isOpen={signatureModalOpen}
        onClose={() => setSignatureModalOpen(false)}
        onUpload={handleUploadSignature}
        isUploading={isUploadingSignature}
        title="Upload Signature"
        description="Upload your signature image"
      />
    </div>
  );
};

interface ImageUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File) => void;
  isUploading: boolean;
  title: string;
  description: string;
}

const ImageUploadModal: React.FC<ImageUploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  isUploading,
  title,
  description,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.webp'],
    },
    maxFiles: 1,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedFile) {
      onUpload(selectedFile);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreview(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <p className="text-sm text-gray-500">{description}</p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!selectedFile ? (
            <div
              {...getRootProps()}
              className={cn(
                'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
                isDragActive
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-300 hover:border-gray-400',
              )}
            >
              <input {...getInputProps()} />
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-3" />
              <p className="text-sm font-medium text-gray-900 mb-1">
                {isDragActive ? 'Drop the image here' : 'Upload image'}
              </p>
              <p className="text-xs text-gray-500">
                Drag & drop or click to browse
              </p>
              <p className="text-xs text-gray-400 mt-2">
                Supports PNG, JPG, JPEG, WebP
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {preview && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <img
                    src={preview}
                    alt="Preview"
                    className="max-w-full max-h-48 object-contain mx-auto"
                  />
                </div>
              )}
              <div className="text-sm text-gray-600">
                <p className="font-medium">{selectedFile.name}</p>
                <p className="text-xs">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedFile(null);
                  setPreview(null);
                }}
              >
                Choose different file
              </Button>
            </div>
          )}

          <div className="flex gap-2 justify-end pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!selectedFile || isUploading}
              className="gap-2"
            >
              {isUploading ? (
                <>
                  <div className="h-3 w-3 animate-spin rounded-full border border-white border-t-transparent" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-3 w-3" />
                  Upload
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
